import { DescricaoCargo, DescritivoReajusteRequestPut, Excluidos, iCriteriosPontuacao, iSolicitacaoReajuste } from '../../../interfaces'
import { IAlteracoes, iItemSolicitado, IStatusAjustes, listarItensSolicitados } from './ajustes'

export type DecisaoPonto = 'aceito' | 'recusado'

export type CampoAjuste =
  | 'missao'
  | 'atribuicoes'
  | 'requisitosTecnicos'
  | 'requisitosComportamentais'
  | 'criterios'
  | 'cargosSuperiores'
  | 'cargosSubordinados'

export interface iPontoSolicitado extends iItemSolicitado {
  chave: string
  campo: CampoAjuste
  solicitacaoId: string
  emailSolicitante: string
  nomeSolicitante: string
  dataSolicitacao?: string
  criteriosSolicitados?: iCriteriosPontuacao[]
}

export interface iGrupoPontos {
  chaveAlvo: string
  categoria: string
  nivel?: string
  // Solicitacoes diferentes pedindo coisas diferentes para o mesmo ponto: so uma pode ser aceita
  temConflito: boolean
  pontos: iPontoSolicitado[]
}

export interface iBlocoCategoria {
  campo: CampoAjuste
  titulo: string
  grupos: iGrupoPontos[]
}

export type MapaDecisoes = Record<string, DecisaoPonto>

const campoPorCategoria: Record<string, CampoAjuste> = {
  Missão: 'missao',
  Atribuições: 'atribuicoes',
  'Conhecimentos técnicos': 'requisitosTecnicos',
  'Requisitos comportamentais': 'requisitosComportamentais',
  'Critérios de pontuação': 'criterios',
  'Cargos superiores': 'cargosSuperiores',
  'Trilhas de carreira': 'cargosSubordinados'
}

export const tituloCampo: Record<CampoAjuste, string> = {
  missao: 'Missão',
  atribuicoes: 'Atribuições da função',
  requisitosTecnicos: 'Conhecimentos técnicos e habilidades',
  requisitosComportamentais: 'Requisitos comportamentais',
  criterios: 'Critérios',
  cargosSuperiores: 'Cargo do superior imediato',
  cargosSubordinados: 'Trilhas de carreira'
}

const ordemCampos: CampoAjuste[] = [
  'missao',
  'atribuicoes',
  'requisitosTecnicos',
  'requisitosComportamentais',
  'criterios',
  'cargosSuperiores',
  'cargosSubordinados'
]

// A tela do assinante troca espaco por underscore na chave do nivel e o back nao desfaz
export function normalizarNivel (nivel?: string): string {
  return (nivel ?? '').replaceAll('_', ' ').trim()
}

function resolverChaveNivel (chaves: string[], nivel?: string): string | undefined {
  const alvo = normalizarNivel(nivel)

  return chaves.find(chave => normalizarNivel(chave) === alvo)
}

// Dois pedidos batem no mesmo ponto quando mexem no mesmo item existente, ou quando
// propoem a inclusao do mesmo texto
function chaveAlvoDoPonto (item: iItemSolicitado): string {
  const base = `${item.categoria}|${normalizarNivel(item.nivel)}`

  if (item.operacao === 'inclusao') {
    return `${base}|+${item.valorSolicitado ?? ''}`
  }

  return `${base}|${item.valorAnterior ?? ''}`
}

function criteriosDaSolicitacao (solicitacao: iSolicitacaoReajuste, nivel?: string): iCriteriosPontuacao[] {
  const niveis = solicitacao.estadoSolicitado?.niveis ?? solicitacao.niveis ?? {}
  const chave = resolverChaveNivel(Object.keys(niveis), nivel)

  return chave !== undefined ? (niveis[chave]?.criteriosPontuacao ?? []) : []
}

// O diff vem dos snapshots congelados, que nao mudam depois da decisao: sem esse corte
// o ponto ja decidido voltaria a aparecer como pendente
function campoJaDecidido (solicitacao: iSolicitacaoReajuste, campo: CampoAjuste): boolean {
  return solicitacao.aprovacaoAjustes?.[campo] === true || solicitacao.reprovacaoAjustes?.[campo] === true
}

export function consolidarPontos (
  cargo: DescricaoCargo | undefined,
  solicitacoes: iSolicitacaoReajuste[],
  nomePorEmail: (email: string) => string
): iBlocoCategoria[] {
  if (!cargo) return []

  const pontos = solicitacoes.flatMap((solicitacao) => {
    const solicitacaoId = solicitacao.solicitacaoId ?? ''

    return listarItensSolicitados(solicitacao, cargo)
      .filter((item) => {
        const campo = campoPorCategoria[item.categoria]

        return campo !== undefined && !campoJaDecidido(solicitacao, campo)
      })
      .map((item, indice): iPontoSolicitado => {
        const campo = campoPorCategoria[item.categoria]

        return {
          ...item,
          campo,
          chave: `${solicitacaoId}|${item.categoria}|${normalizarNivel(item.nivel)}|${item.operacao}|${item.valorAnterior ?? ''}|${item.valorSolicitado ?? ''}|${indice}`,
          solicitacaoId,
          emailSolicitante: solicitacao.emailSolicitante,
          nomeSolicitante: nomePorEmail(solicitacao.emailSolicitante),
          dataSolicitacao: solicitacao.dataSolicitacao,
          criteriosSolicitados: campo === 'criterios' ? criteriosDaSolicitacao(solicitacao, item.nivel) : undefined
        }
      })
  })

  const grupos = new Map<string, iGrupoPontos>()

  pontos.forEach((ponto) => {
    const chaveAlvo = `${ponto.campo}|${chaveAlvoDoPonto(ponto)}`
    const grupo = grupos.get(chaveAlvo)

    if (grupo === undefined) {
      grupos.set(chaveAlvo, {
        chaveAlvo,
        categoria: ponto.categoria,
        nivel: ponto.nivel,
        temConflito: false,
        pontos: [ponto]
      })
      return
    }

    grupo.pontos.push(ponto)
  })

  grupos.forEach((grupo) => {
    const desfechos = new Set(grupo.pontos.map(ponto => `${ponto.operacao}|${ponto.valorSolicitado ?? ''}|${ponto.criteriosSolicitados !== undefined ? ponto.solicitacaoId : ''}`))

    grupo.temConflito = desfechos.size > 1
  })

  return ordemCampos
    .map(campo => ({
      campo,
      titulo: tituloCampo[campo],
      grupos: Array.from(grupos.values()).filter(grupo => campoPorCategoria[grupo.categoria] === campo)
    }))
    .filter(bloco => bloco.grupos.length > 0)
}

export function pontosDoBloco (bloco: iBlocoCategoria): iPontoSolicitado[] {
  return bloco.grupos.flatMap(grupo => grupo.pontos)
}

// Um grupo em conflito aceita no maximo um ponto: aceitar outro troca a escolha
export function decidirPonto (
  decisoes: MapaDecisoes,
  grupo: iGrupoPontos,
  ponto: iPontoSolicitado,
  decisao: DecisaoPonto
): MapaDecisoes {
  // Clicar de novo na mesma decisao volta o ponto para pendente
  if (decisoes[ponto.chave] === decisao) {
    return Object.fromEntries(
      Object.entries(decisoes).filter(([chave]) => chave !== ponto.chave)
    )
  }

  const atualizadas: MapaDecisoes = { ...decisoes, [ponto.chave]: decisao }

  if (decisao === 'aceito' && grupo.temConflito) {
    grupo.pontos
      .filter(outro => outro.chave !== ponto.chave)
      .forEach(outro => { atualizadas[outro.chave] = 'recusado' })
  }

  return atualizadas
}

function aplicarNaLista (atual: string[], aceitos: iPontoSolicitado[]): string[] {
  let lista = atual.filter(Boolean)

  aceitos
    .filter(ponto => ponto.operacao === 'alteracao')
    .forEach((ponto) => {
      const indice = lista.indexOf(ponto.valorAnterior ?? '')
      const novo = ponto.valorSolicitado ?? ''

      if (indice >= 0) {
        lista[indice] = novo
        return
      }

      if (novo !== '' && !lista.includes(novo)) lista.push(novo)
    })

  aceitos
    .filter(ponto => ponto.operacao === 'remocao')
    .forEach((ponto) => {
      lista = lista.filter(valor => valor !== ponto.valorAnterior)
    })

  aceitos
    .filter(ponto => ponto.operacao === 'inclusao')
    .forEach((ponto) => {
      const novo = ponto.valorSolicitado ?? ''

      if (novo !== '' && !lista.includes(novo)) lista.push(novo)
    })

  return lista
}

function exclusoesDecididas (
  solicitacao: iSolicitacaoReajuste | undefined,
  pontos: iPontoSolicitado[],
  decisoes: MapaDecisoes
): Excluidos[] {
  return pontos
    .map((ponto) => {
      const exclusao = solicitacao?.excluidosMotivos?.find(e => e.campoExcluido === ponto.caminho)

      if (exclusao === undefined) return undefined

      return { ...exclusao, aceito: decisoes[ponto.chave] === 'aceito' }
    })
    .filter((exclusao): exclusao is NonNullable<typeof exclusao> => exclusao !== undefined)
}

function montarNiveis (
  cargo: DescricaoCargo,
  pontosDecididos: iPontoSolicitado[],
  decisoes: MapaDecisoes,
  campo: 'atribuicoes' | 'conhecimentosTecnicos'
): Record<string, { atribuicoes?: string[], conhecimentosTecnicos?: string[] }> {
  const chavesCargo = Object.keys(cargo.niveis ?? {})
  const niveis: Record<string, { atribuicoes?: string[], conhecimentosTecnicos?: string[] }> = {}

  chavesCargo.forEach((chaveNivel) => {
    const pontosDoNivel = pontosDecididos.filter(ponto => normalizarNivel(ponto.nivel) === normalizarNivel(chaveNivel))

    if (pontosDoNivel.length === 0) return

    const aceitos = pontosDoNivel.filter(ponto => decisoes[ponto.chave] === 'aceito')
    const listaFinal = aplicarNaLista(cargo.niveis[chaveNivel]?.[campo] ?? [], aceitos)

    niveis[chaveNivel] = campo === 'atribuicoes'
      ? { atribuicoes: listaFinal }
      : { conhecimentosTecnicos: listaFinal }
  })

  return niveis
}

function montarCriterios (
  cargo: DescricaoCargo,
  pontosDecididos: iPontoSolicitado[],
  decisoes: MapaDecisoes
): Record<string, { criteriosPontuacao: iCriteriosPontuacao[] }> {
  const niveis: Record<string, { criteriosPontuacao: iCriteriosPontuacao[] }> = {}

  Object.keys(cargo.niveis ?? {}).forEach((chaveNivel) => {
    const pontosDoNivel = pontosDecididos.filter(ponto => normalizarNivel(ponto.nivel) === normalizarNivel(chaveNivel))

    if (pontosDoNivel.length === 0) return

    // Criterio nao tem diff item a item: o aceite troca a pontuacao inteira daquele nivel
    const aceito = pontosDoNivel.find(ponto => decisoes[ponto.chave] === 'aceito')

    niveis[chaveNivel] = {
      criteriosPontuacao: aceito?.criteriosSolicitados ?? cargo.niveis[chaveNivel]?.criteriosPontuacao ?? []
    }
  })

  return niveis
}

// Uma requisicao por categoria: o backend marca aprovacao ou reprovacao por categoria,
// entao misturar categorias na mesma chamada apagaria a decisao de uma delas
export function montarPayloadsDecisao (
  cargo: DescricaoCargo,
  blocos: iBlocoCategoria[],
  decisoes: MapaDecisoes,
  solicitacoes: iSolicitacaoReajuste[]
): DescritivoReajusteRequestPut[] {
  return blocos
    .map((bloco) => {
      const pontosDecididos = pontosDoBloco(bloco).filter(ponto => decisoes[ponto.chave] !== undefined)

      if (pontosDecididos.length === 0) return undefined

      const aceitos = pontosDecididos.filter(ponto => decisoes[ponto.chave] === 'aceito')

      const idsEnvolvidos = Array.from(new Set(pontosDecididos.map(ponto => ponto.solicitacaoId)))

      const payload: DescritivoReajusteRequestPut = {
        cargoId: cargo.id,
        solicitacaoId: idsEnvolvidos[0] ?? '',
        decisoes: idsEnvolvidos.map((solicitacaoId) => {
          const pontosDaSolicitacao = pontosDecididos.filter(ponto => ponto.solicitacaoId === solicitacaoId)

          return {
            solicitacaoId,
            aceito: pontosDaSolicitacao.some(ponto => decisoes[ponto.chave] === 'aceito'),
            excluidosMotivos: exclusoesDecididas(
              solicitacoes.find(s => s.solicitacaoId === solicitacaoId),
              pontosDaSolicitacao,
              decisoes
            )
          }
        })
      }

      switch (bloco.campo) {
        case 'missao':
          payload.missao = aceitos[0]?.valorSolicitado ?? cargo.missao ?? ''
          break

        case 'requisitosComportamentais':
          payload.competenciasComportamentais = aplicarNaLista(cargo.competenciasComportamentais ?? [], aceitos)
          break

        case 'atribuicoes':
          payload.niveis = montarNiveis(cargo, pontosDecididos, decisoes, 'atribuicoes')
          break

        case 'requisitosTecnicos':
          payload.niveis = montarNiveis(cargo, pontosDecididos, decisoes, 'conhecimentosTecnicos')
          break

        case 'criterios':
          payload.niveis = montarCriterios(cargo, pontosDecididos, decisoes)
          break

        case 'cargosSuperiores': {
          const atuais = (cargo.cargosSuperiores ?? []).filter(c => c.cargoSuperiorId)

          let lista = atuais.filter(atual =>
            !aceitos.some(ponto => (ponto.operacao === 'remocao' || ponto.operacao === 'alteracao') && ponto.idAnterior === atual.cargoSuperiorId)
          )

          aceitos
            .filter(ponto => (ponto.operacao === 'inclusao' || ponto.operacao === 'alteracao') && ponto.idSolicitado !== undefined)
            .forEach((ponto) => {
              if (!lista.some(c => c.cargoSuperiorId === ponto.idSolicitado)) {
                lista = [...lista, { cargoSuperiorId: ponto.idSolicitado ?? '', nome: ponto.valorSolicitado }]
              }
            })

          payload.cargosSuperiores = lista
          break
        }

        case 'cargosSubordinados': {
          // Trilha e identificada por area+cargo: mesma chave usada pra gerar o diff.
          // Uma area pode ter mais de uma linha (ex: estagiario -> jr -> pleno no mesmo cargo)
          const mesmaTrilha = (a?: { areaId: string, cargoSuperiorId?: string }, b?: { areaId: string, cargoSuperiorId?: string }): boolean =>
            a?.areaId === b?.areaId && (a?.cargoSuperiorId ?? '') === (b?.cargoSuperiorId ?? '')

          let lista = (cargo.trilhasDeCarreira ?? []).filter(atual =>
            !aceitos.some(ponto => (ponto.operacao === 'remocao' || ponto.operacao === 'alteracao') && mesmaTrilha(ponto.trilhaAnterior, atual))
          )

          aceitos
            .forEach((ponto) => {
              const nova = ponto.trilhaSolicitada

              if (nova === undefined) return

              if ((ponto.operacao === 'inclusao' || ponto.operacao === 'alteracao') && !lista.some(trilha => mesmaTrilha(trilha, nova))) {
                lista = [...lista, nova]
              }
            })

          payload.trilhasDeCarreira = lista
          break
        }
      }

      return payload
    })
    .filter((payload): payload is DescritivoReajusteRequestPut => payload !== undefined)
}

// Badges e destaque em vermelho saem dos MESMOS pontos do modal.
// Enquanto eram calculados por caminhos diferentes, a tela marcava alteracao que a analise nao mostrava
export function derivarStatusAjustes (
  cargo: DescricaoCargo | undefined,
  blocos: iBlocoCategoria[]
): IStatusAjustes {
  const vazio = (): IAlteracoes => ({ temAlteracao: false, quantidadeAlteracoes: 0 })

  const status: IStatusAjustes = {
    missao: vazio(),
    cargosSuperiores: vazio(),
    cargosSubordinados: vazio(),
    atribuicoes: vazio(),
    requisitosTecnicos: vazio(),
    criterios: vazio(),
    requisitosComportamentais: vazio()
  }

  if (!cargo) return status

  const chavesNivelDoCargo = Object.keys(cargo.niveis ?? {})

  // O dicionario e indexado pela chave do cargo, que e a que a tabela usa para se pintar
  const chaveDoCargoPara = (nivel?: string): string | undefined =>
    chavesNivelDoCargo.find(chave => normalizarNivel(chave) === normalizarNivel(nivel))

  blocos.forEach((bloco) => {
    const pontos = pontosDoBloco(bloco)

    if (pontos.length === 0) return

    const alteracoes = Array.from(new Set(
      pontos
        .filter(ponto => ponto.operacao !== 'inclusao')
        .map(ponto => ponto.valorAnterior ?? '')
        .filter(valor => valor !== '')
    ))

    const alteracoesInclusao = Array.from(new Set(
      pontos
        .filter(ponto => ponto.operacao !== 'remocao')
        .map(ponto => ponto.valorSolicitado ?? '')
        .filter(valor => valor !== '')
    ))

    const porNivel = (selecionar: (ponto: iPontoSolicitado) => string | undefined): Record<string, string[]> => {
      const dicionario: Record<string, string[]> = {}

      pontos.forEach((ponto) => {
        const chave = chaveDoCargoPara(ponto.nivel)
        const valor = selecionar(ponto)

        if (chave === undefined || valor === undefined || valor === '') return

        dicionario[chave] = Array.from(new Set([...(dicionario[chave] ?? []), valor]))
      })

      return dicionario
    }

    status[bloco.campo] = {
      temAlteracao: true,
      quantidadeAlteracoes: pontos.length,
      alteracoes,
      alteracoesInclusao,
      alteracoesDicionario: porNivel(ponto => ponto.operacao === 'inclusao' ? undefined : ponto.valorAnterior),
      alteracoesInclusaoDicionario: porNivel(ponto => ponto.operacao === 'remocao' ? undefined : ponto.valorSolicitado),
      solicitacoesId: Array.from(new Set(pontos.map(ponto => ponto.solicitacaoId)))
    }
  })

  // Criterio nao tem diff item a item: a tabela marca a celula do nivel que mudou
  const blocoCriterios = blocos.find(bloco => bloco.campo === 'criterios')

  if (blocoCriterios !== undefined) {
    const dicionario: Record<string, string[]> = {}

    pontosDoBloco(blocoCriterios).forEach((ponto) => {
      const chave = chaveDoCargoPara(ponto.nivel)

      if (chave === undefined) return

      const atuais = cargo.niveis?.[chave]?.criteriosPontuacao ?? []
      const solicitados = ponto.criteriosSolicitados ?? []

      const diferentes = atuais
        .filter(atual => !solicitados.some(solicitado =>
          solicitado.criterioId === atual.criterioId &&
          solicitado.opcaoPontuacaoId === atual.opcaoPontuacaoId &&
          solicitado.minimo === atual.minimo
        ))
        .map(atual => `${atual.criterioId}|${atual.minimo === true}`)

      dicionario[chave] = Array.from(new Set([...(dicionario[chave] ?? []), ...diferentes]))
    })

    status.criterios.alteracoesDicionario = dicionario
  }

  return status
}
